<template>
  <div class="boost-facet-carousel">
    <template v-if="ready">
      <swiper
        class="boost-facet-carousel__content"
        :slides-per-view="swiperOptions.slidesPerView"
        :space-between="swiperOptions.spaceBetween"
        :loop="swiperOptions.loop"
        :breakpoints="swiperOptions.breakpoints"
        a11y
        :modules="modules"
        :navigation="{
          prevEl: $refs.prevButton,
          nextEl: $refs.nextButton,
        }"
        @swiper="onSwiper"
      >
        <swiper-slide
          v-for="(facet, index) of facetCarousel"
          :key="`facet-tile-${index}`"
        >
          <div
            class="boost-facet-carousel__slide-inner"
          >
            <responsive-image
              :class="$blockClass(blockClass, 'boost-facet-carousel')"
              block-class="boost-facet-carousel"
              :image="getImage(facet)"
              :lazy-load="false"
              ratio="320:442"
              sizes="100vw"
              @click="handleTileClick(facet)"
            />
            <button
              v-if="facet.title"
              type="button"
              class="boost-facet-carousel__title"
              :style="{color: facet.color}"
              @click.prevent="handleTileClick(facet)"
              v-text="facet.title"
            />
            <div
              v-if="facet.subtitle"
              class="boost-facet-carousel__subtitle"
              v-text="facet.subtitle"
            />
          </div>
        </swiper-slide>
      </swiper>
    </template>

    <div class="boost-facet-carousel__navigation">
      <button
        ref="prevButton"
        type="button"
        class="
          boost-facet-carousel__button
          boost-facet-carousel__button--prev
          button
          button--nav-arrow
        "
        :aria-label="$string('accessibility.carousel.next')"
      >
        <span
          class="button__label visually-hidden"
          v-text="$string('accessibility.carousel.next')"
        />

        <icon-arrow-left-narrow />
      </button>

      <button
        ref="nextButton"
        type="button"
        class="
          boost-facet-carousel__button
          boost-facet-carousel__button--next
          button
          button--nav-arrow
        "
        :aria-label="$string('accessibility.carousel.next')"
      >
        <span
          class="button__label visually-hidden"
          v-text="$string('accessibility.carousel.next')"
        />

        <icon-arrow-right-narrow />
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Boost facet carousel (boost-facet-carousel)
 * -----------------------------------------------------------------------------
 * Carousel that links to boost facets.
 *
 * @param {Array} facetCarousel - Carousel content
 *
 */

import { mapState } from 'vuex'

import { A11y, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import ResponsiveImage from '~global/images/responsive-image.vue'

import IconArrowLeftNarrow from '~icons/direction/chevron/left-narrow.svg'
import IconArrowRightNarrow from '~icons/direction/chevron/right-narrow.svg'

export default {
  name: 'BoostFacetCarousel',

  components: {
    IconArrowLeftNarrow,
    IconArrowRightNarrow,
    ResponsiveImage,
    Swiper,
    SwiperSlide,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    facetCarousel: {
      type: Array,
      default: null,
    },
  },

  emits: {
    'carousel-facet': (payload) => typeof payload === 'string',
  },

  setup() {
    return {
      modules: [
        A11y,
        Navigation,
      ],
    }
  },

  data() {
    return {
      ready: false,
      swiper: null,
      swiperOptions: {
        slidesPerView: 2.12,
        spaceBetween: 4,
        loop: true,
        breakpoints: {
          1024: {
            slidesPerView: 4.425,
            loop: false,
            spaceBetween: 6,
          },
        },
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.ready = true
  },

  methods: {

    /**
     * Cache swiper after rendering.
     * @param {Object} swiper - data from swiper
     */
    onSwiper(swiper) {
      this.swiper = swiper
    },

    /**
     * Get image
     * @param {Object} facet - Facet data
     * @return {Object}
     */
    getImage(facet) {
      if (!facet.image) {
        return null
      }

      return {
        src: facet.image,
        alt: facet.imageAlt,
      }
    },

    /**
     * Handle tile click and emit data.
     * @param {Object} facet - Facet data
     */
    handleTileClick(facet) {
      const { id } = facet
      this.$emit('carousel-facet', id)
    },
  },
}
</script>

<style lang="scss">
@import './boost-facet-carousel';
</style>
